// Google Error Reporting setup for front-end
import { isProduction } from "../../firebaseConfig.js";

// Internal singleton instance holder (must be declared in module scope for strict mode)
let errorReporting = null;
let __forceReporting = false; // test hook

function _setForceEnableReporting(v) {
  __forceReporting = Boolean(v);
}

// Browser-compatible error reporting using fetch API
class BrowserErrorReporting {
  constructor(projectId, serviceContext) {
    this.projectId = projectId;
    this.serviceContext = serviceContext;
    this.apiUrl = `https://clouderrorreporting.googleapis.com/v1beta1/projects/${projectId}/events:report`;
  }

  async report(error, context = {}) {
    // Only report in production or if explicitly enabled
    if (!isProduction && !__forceReporting && !window.location.search.includes("enableErrorReporting=true")) {
      console.warn("Error would be reported in production:", error);
      return;
    }

    try {
      const errorEvent = {
        message: error.message || "Unknown error",
        serviceContext: this.serviceContext,
        context: {
          httpRequest: context.httpRequest || {},
          user: context.user || "",
          reportLocation: {
            filePath: error.filename || context.filePath || "unknown",
            lineNumber: error.lineno || context.lineNumber || 0,
            functionName: context.functionName || "unknown",
          },
        },
        eventTime: new Date().toISOString(),
      };

      // Add stack trace if available
      if (error.stack) {
        errorEvent.message += "\n\nStack trace:\n" + error.stack;
      }

      // Report to console for debugging
      console.error("Reporting error:", errorEvent);

      // In a real implementation, you would send this to Google Error Reporting API
      // For now, we'll log it and store locally for demonstration
      const errorLog = {
        timestamp: new Date().toISOString(),
        error: errorEvent,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Store in localStorage for demonstration (in production, send to API)
      const existingErrors = JSON.parse(localStorage.getItem("errorReports") || "[]");
      existingErrors.push(errorLog);
      // Keep only last 50 errors
      const recentErrors = existingErrors.slice(-50);
      localStorage.setItem("errorReports", JSON.stringify(recentErrors));
    } catch (reportingError) {
      console.error("Failed to report error:", reportingError);
    }
  }
}

// Initialize error reporting
function initializeErrorReporting() {
  const projectId = isProduction ? "data-sculpture" : "data-sculpture-ai";
  const serviceContext = {
    service: "ds-frontend",
    version: "1.0.0",
  };

  errorReporting = new BrowserErrorReporting(projectId, serviceContext);

  // Set up global error handlers
  window.addEventListener("error", (event) => {
    errorReporting.report(event.error || new Error(event.message), {
      filePath: event.filename,
      lineNumber: event.lineno,
      functionName: "global_error_handler",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    errorReporting.report(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
      functionName: "unhandled_promise_rejection",
    });
  });

  // console.log("Error reporting initialized for project:", projectId);
}

// Function to manually report errors
function reportError(error, context = {}) {
  if (errorReporting) {
    errorReporting.report(error, context);
  } else {
    console.error("Error reporting not initialized:", error);
  }
}

// Function to report custom events/metrics
function reportCustomEvent(eventName, details = {}) {
  if (errorReporting) {
    const customError = new Error(`Custom Event: ${eventName}`);
    errorReporting.report(customError, {
      functionName: "custom_event",
      customData: details,
    });
  }
}

// Function to get stored error reports (for testing/debugging)
function getStoredErrorReports() {
  return JSON.parse(localStorage.getItem("errorReports") || "[]");
}

// Function to clear stored error reports
function clearStoredErrorReports() {
  localStorage.removeItem("errorReports");
}

export { initializeErrorReporting, reportError, reportCustomEvent, getStoredErrorReports, clearStoredErrorReports, _setForceEnableReporting };
